import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, CheckCircle, LogOut } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import AvailablePlansTab from "@/components/pricing/AvailablePlansTab";
import PricingFAQ from "@/components/pricing/PricingFAQ";
import SpecialOfferBanner from "@/components/pricing/SpecialOfferBanner";
import { analytics } from "@/hooks/useAnalytics";
import { fetchPlans, PlanWithFeatures } from "@/services/planService";

export default function PaymentPage() {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [plans, setPlans] = useState<PlanWithFeatures[]>([]);
  const { user, organization, userProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isMountedRef = useRef(true);

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  useEffect(() => {
    // Set mounted flag
    isMountedRef.current = true;

    const loadPlans = async () => {
      try {
        setLoading(true);
        const { data: plansData, error: plansError } = await fetchPlans();

        if (plansError) {
          throw plansError;
        }

        if (plansData) {
          // Filter to only show starter package
          const starterPlans = plansData.filter(
            (plan) => plan.name.toLowerCase() === "starter",
          );
          setPlans(starterPlans);
        }

        // Track InitiateCheckout when payment page loads
        analytics.trackCheckoutInitiated({
          value: 90,
          currency: "USD",
          content_ids: ["hubflo-subscription"],
          contents: [{ id: "hubflo-subscription", quantity: 1 }],
          content_type: "product",
          content_name: "Hubflo AI Subscription",
          content_category: "subscription",
          num_items: 1,
          // Enhanced user data
          email: user?.email || undefined,
          firstName: userProfile?.first_name || undefined,
          lastName: userProfile?.last_name || undefined,
          organizationName: organization?.name || undefined,
          organizationId: organization?.id || undefined,
          userId: user?.id || undefined,
        });
      } catch (error: any) {
        console.error("Error loading plans:", error);
        toast({
          variant: "destructive",
          title: "Error loading plans",
          description: error.message,
        });
      } finally {
        setLoading(false);
      }
    };

    loadPlans();

    // Cleanup function
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleSubscribe = async (planId: string) => {
    if (!organization?.id) {
      toast({
        variant: "destructive",
        title: "Organization not found",
        description: "Please try logging in again.",
      });
      return;
    }

    setProcessing(true);
    try {
      // Use Supabase Edge Function to handle Stripe logic securely
      const { data, error } = await supabase.functions.invoke(
        "create-stripe-checkout",
        {
          body: {
            organizationId: organization.id,
            planId,
          },
        },
      );

      // Check for both types of errors
      if (error) {
        throw error;
      }

      // Check for errors in the response data
      if (data?.error) {
        throw new Error(data.error);
      }

      if (!data?.url) {
        throw new Error("No session URL returned");
      }

      // Redirect to Stripe session URL
      window.location.href = data.url;
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error creating subscription",
        description: error.message,
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      toast({
        variant: "destructive",
        title: "Logout Error",
        description: "There was an issue logging out. Please try again.",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-t from-amber-100/30 via-amber-50/20 to-white relative overflow-hidden flex items-center justify-center">
        {/* Background elements */}
        <div className="absolute -left-16 -top-16 w-32 h-32 bg-gradient-to-t from-yellow-400 to-amber-300 rounded-full opacity-10 blur-2xl"></div>
        <div className="absolute -right-16 -bottom-16 w-32 h-32 bg-gradient-to-t from-amber-400 to-yellow-300 rounded-full opacity-10 blur-2xl"></div>

        {/* WhatsApp-style Background Pattern Overlay */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/image_library/chat_backgrounds/Whatsapp-Style-Background.svg)`,
            backgroundSize: "97%",
            backgroundPosition: "center center",
            backgroundRepeat: "repeat",
            opacity: 0.02,
            zIndex: 1,
            pointerEvents: "none",
            filter: "sepia(0.3) saturate(0.8) hue-rotate(20deg)",
          }}
        />

        <Loader2 className="h-8 w-8 animate-spin relative z-10" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-t from-amber-100/30 via-amber-50/20 to-white relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute -left-16 -top-16 w-32 h-32 bg-gradient-to-t from-yellow-400 to-amber-300 rounded-full opacity-10 blur-2xl"></div>
      <div className="absolute -right-16 -bottom-16 w-32 h-32 bg-gradient-to-t from-amber-400 to-yellow-300 rounded-full opacity-10 blur-2xl"></div>

      {/* WhatsApp-style Background Pattern Overlay */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/image_library/chat_backgrounds/Whatsapp-Style-Background.svg)`,
          backgroundSize: "97%",
          backgroundPosition: "center center",
          backgroundRepeat: "repeat",
          opacity: 0.02,
          zIndex: 1,
          pointerEvents: "none",
          filter: "sepia(0.3) saturate(0.8) hue-rotate(20deg)",
        }}
      />

      {/* Top Navbar */}
      <nav
        className="w-full h-16 flex items-center justify-center px-6 relative z-10"
        style={{ backgroundColor: "#060520" }}
      >
        <img
          src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/image_library/public_images/logo%20horizontal%20white%20agents.svg`}
          alt="Hubflo Logo"
          className="h-8 w-auto object-contain"
        />
        <div className="absolute right-6 top-1/2 transform -translate-y-1/2">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            className="text-gray-300 hover:text-white transition-colors"
            title="Sign out"
          >
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container mx-auto px-4 max-w-7xl pb-24 pt-16 relative z-10">
        <div className="text-center mb-12 max-w-4xl mx-auto">
          <h1
            className="font-bold tracking-tight text-gray-900 mb-4"
            style={{ fontSize: "1.8rem", lineHeight: "1.1" }}
          >
            <span className="sm:text-[2.5rem]">
              Your AI assistants are ready to help grow{" "}
              <span style={{ color: "#eab308" }}>
                {organization?.name || "your organization"}
              </span>{" "}
              🚀
            </span>
          </h1>
          <p className="text-base text-gray-600">
            Get started now with a free 3-day trial—no credit card required.
          </p>
        </div>

        <div id="plans-section">
          <PaymentPagePricingCards
            plans={plans}
            organization={organization}
            processing={processing}
            onSubscribe={handleSubscribe}
          />
        </div>

        {/* Benefits Block - Moved after plans */}
        {/* <div className="max-w-4xl mx-auto mb-16 mt-12 relative z-10">
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              <div className="flex items-center justify-center p-8">
                <img
                  src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/image_library/public_images/2%20agents%20animation%20gif.gif`}
                  alt="AI Agents Animation"
                  className="w-full h-auto max-w-sm"
                />
              </div>

              <div className="p-8 pb-12 flex flex-col justify-center">
                <div className="space-y-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle
                      className="w-6 h-6 mt-1 flex-shrink-0"
                      style={{ color: "#eab308" }}
                    />
                    <div>
                      <h4 className="font-medium text-base mb-1 text-gray-900">
                        Bring in more qualified leads
                      </h4>
                      <p className="text-sm text-gray-600 font-normal leading-relaxed">
                        Agents engage instantly, day and night — qualifying
                        leads and booking calls on autopilot.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle
                      className="w-6 h-6 mt-1 flex-shrink-0"
                      style={{ color: "#eab308" }}
                    />
                    <div>
                      <h4 className="font-medium text-base mb-1 text-gray-900">
                        Save time on client support
                      </h4>
                      <p className="text-sm text-gray-600 font-normal leading-relaxed">
                        Agents handle up to 50% of client questions — fewer
                        calls and emails, more time for real work.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle
                      className="w-6 h-6 mt-1 flex-shrink-0"
                      style={{ color: "#eab308" }}
                    />
                    <div>
                      <h4 className="font-medium text-base mb-1 text-gray-900">
                        Improve your client experience
                      </h4>
                      <p className="text-sm text-gray-600 font-normal leading-relaxed">
                        Instant, helpful, and human-like interactions — every
                        time a lead or client reaches out.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        {/* Testimonial Section */}
        {/* <div className="max-w-4xl mx-auto mt-16 mb-16 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-3xl p-8 sm:p-10 shadow-sm border border-gray-100">
              <div className="flex justify-start mb-6">
                <svg
                  className="w-10 h-10 text-gray-300"
                  fill="currentColor"
                  viewBox="0 0 32 32"
                >
                  <path d="M10 8c-3.314 0-6 2.686-6 6v10h8V14h-4c0-1.105.895-2 2-2V8zm12 0c-3.314 0-6 2.686-6 6v10h8V14h-4c0-1.105.895-2 2-2V8z" />
                </svg>
              </div>

              <blockquote>
                <p className="text-base sm:text-lg font-normal text-gray-900 leading-relaxed mb-6 italic">
                  "Setting up our AI employee Lexi was incredibly simple. She's
                  doing an amazing job engaging our website visitors and
                  qualifying leads automatically. I'd say we easily doubled the
                  number of qualified leads!"
                </p>

                <div className="flex items-center gap-4">
                  <img
                    src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/image_library/public_images/CleanShot%202025-06-04%20at%2000.55.16@2x.png`}
                    alt="Elena Chen"
                    className="w-12 h-12 rounded-full object-cover"
                  />

                  <div>
                    <div className="font-semibold text-gray-900 text-base">
                      Elena Chen
                    </div>
                    <div className="text-gray-600 text-sm">
                      Managing Director | GrowthState
                    </div>
                  </div>
                </div>
              </blockquote>
            </div>

            <div className="bg-white rounded-3xl p-8 sm:p-10 shadow-sm border border-gray-100">
              <div className="flex justify-start mb-6">
                <svg
                  className="w-10 h-10 text-gray-300"
                  fill="currentColor"
                  viewBox="0 0 32 32"
                >
                  <path d="M10 8c-3.314 0-6 2.686-6 6v10h8V14h-4c0-1.105.895-2 2-2V8zm12 0c-3.314 0-6 2.686-6 6v10h8V14h-4c0-1.105.895-2 2-2V8z" />
                </svg>
              </div>

              <blockquote>
                <p className="text-base sm:text-lg font-normal text-gray-900 leading-relaxed mb-6 italic">
                  "Our AI agent has drastically reduced the number of calls,
                  texts, and emails we get from clients. He handles dozens of
                  interactions every week, and when something needs our
                  attention, he passes it along with all the context."
                </p>

                <div className="flex items-center gap-4">
                  <img
                    src={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/image_library/public_images/testimonial_3.png`}
                    alt="Chloe McDonell"
                    className="w-12 h-12 rounded-full object-cover"
                  />

                  <div>
                    <div className="font-semibold text-gray-900 text-base">
                      Chloe McDonell
                    </div>
                    <div className="text-gray-600 text-sm">
                      Owner | The Creative Crew
                    </div>
                  </div>
                </div>
              </blockquote>
            </div>
          </div>
        </div> */}

        {/* <PricingFAQ />

        <div className="text-center mt-8">
          <p className="text-sm text-muted-foreground">
            You can upgrade or downgrade your plan at any time.
          </p>
        </div> */}
      </div>

      {/* Special Offer Banner */}
      <SpecialOfferBanner />
    </div>
  );
}

// Payment Page specific pricing component that shows all active plans
interface PaymentPagePricingCardsProps {
  plans: PlanWithFeatures[];
  organization: any;
  processing: boolean;
  onSubscribe: (planId: string) => void;
}

const PaymentPagePricingCards: React.FC<PaymentPagePricingCardsProps> = ({
  plans,
  organization,
  processing,
  onSubscribe,
}) => {
  const handleSubscribe = (planId: string) => {
    onSubscribe(planId);
  };

  const isPlanCurrent = (planId: string) => {
    return organization?.plan_id === planId;
  };

  // Transform database plans to display format matching website
  const transformedPlans = plans.map((plan) => {
    let features: string[] = [];
    let everythingInPrevious: string | undefined;
    let additionalFeatures: string[] | undefined;

    if (plan.name === "Starter") {
      features = [
        "30+ ready-to-use AI assistants powered by the latest models",
        "Create your own assistants",
        "700 AI outputs/month",
        "Live web access",
        "Share assistants publicly",
        "Customize assistants",
      ];
    } else if (plan.name === "Pro") {
      everythingInPrevious = "Everything in STARTER +";
      additionalFeatures = [
        "2,000 AI outputs/month",
        "AI Phone Agents",
        "450 voice minutes (~4/6 calls/day)",
        "$0.3/min overage",
        "Teams: Invite additional users ($19/u/m)",
        "Max 15 knowledge sources (files, websites, snippets)",
        "Connect to your CRM and 5,000 other tools",
        "Priority support",
      ];
    } else if (plan.name === "Business") {
      everythingInPrevious = "Everything in PRO +";
      additionalFeatures = [
        "4,000 AI outputs/month",
        "More AI Phone agents",
        "1,500 voice minutes",
        "$0.21/min overage",
        "Advanced integrations",
        "Custom voice cloning",
        "Dedicated account manager",
      ];
    }

    return {
      id: plan.id,
      name: plan.name,
      price: plan.monthly_price,
      features,
      everythingInPrevious,
      additionalFeatures,
    };
  });

  const sharedFeatures = [
    "Chat branding (logo, colors)",
    "Collect information (fields)",
    "Conversation inbox + Email alerts",
    "Zapier / webhook integration",
    "Live access to your website",
  ];

  // Show message if no plans available
  if (transformedPlans.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No plans available at the moment.</p>
        <p className="text-sm text-gray-400 mt-2">
          Please refresh the page to try again.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex justify-center items-center mb-8">
        {transformedPlans.map((plan) => (
          <div
            key={plan.id}
            className={`flex flex-col relative bg-white rounded-3xl shadow-sm border border-gray-100 w-full max-w-sm mx-auto ${
              organization?.plan_id === plan.id
                ? "ring-2 ring-primary border-primary"
                : ""
            }`}
          >
            {organization?.plan_id === plan.id && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium">
                Current Plan
              </div>
            )}
            <div className="p-6">
              <div className="text-2xl font-bold text-center">{plan.name}</div>
              <div className="mt-6 text-center">
                <div className="flex items-baseline justify-center">
                  {plan.name === "Starter" && (
                    <span className="text-3xl font-bold tracking-tight text-gray-400 line-through mr-2">
                      $69
                    </span>
                  )}
                  <span
                    className="text-5xl font-bold tracking-tight"
                    style={{ color: "#8aa3d9" }}
                  >
                    ${plan.name === "Starter" ? "34" : plan.price}
                  </span>
                  <span className="ml-2 text-xl text-gray-500">/mth</span>
                </div>
                {plan.name === "Starter" && (
                  <div className="text-sm text-red-600 font-medium mt-2">
                    Lifetime discount - Offer ends Sept 3rd
                  </div>
                )}
              </div>
              <div className="mt-6 mb-8"></div>

              <Button
                className="w-full text-base"
                size="lg"
                onClick={() => handleSubscribe(plan.id)}
                disabled={processing || isPlanCurrent(plan.id)}
                style={{ backgroundColor: "#8aa3d9", borderColor: "#8aa3d9" }}
                onMouseEnter={(e) => {
                  if (!processing && !isPlanCurrent(plan.id)) {
                    e.currentTarget.style.backgroundColor = "#7691d1";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!processing && !isPlanCurrent(plan.id)) {
                    e.currentTarget.style.backgroundColor = "#8aa3d9";
                  }
                }}
              >
                {processing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : isPlanCurrent(plan.id) ? (
                  "Current Plan"
                ) : (
                  "Start free trial"
                )}
              </Button>

              <div className="flex items-center justify-center mt-2">
                <div className="h-4 w-4 mr-1" style={{ color: "#8aa3d9" }}>
                  ✓
                </div>
                <span className="text-sm text-gray-500">
                  No credit card required
                </span>
              </div>
            </div>

            <div className="flex-grow p-6 pt-0">
              {/* Plan Features */}
              <ul className="space-y-2 mb-6">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm font-medium">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {/* Included in All Subscriptions */}
      {/* <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm max-w-5xl mx-auto">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 text-left">
          Included in all subscriptions
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sharedFeatures.map((feature, index) => (
            <div key={index} className="flex items-start">
              <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-gray-700">{feature}</span>
            </div>
          ))}
        </div>
      </div> */}
    </div>
  );
};
